import {
  CreateProductPayload,
  EditableAttributeMode,
  EditableAttributeOption,
  EditableProductAttribute,
  ProductAttributeDefinition,
  ProductAttributeValue,
  ProductDiscountPrice,
  ProductMoney,
  ProductVariantPayload,
  ProductWholesaleOffer,
  UpdateProductPayload,
  Variant,
} from "../../../../types/ProductTypes";

export const PRODUCT_CURRENCY = "CAD" as const;

type SelectedImage = {
  url?: string;
  otherNames?: string[];
  othernames?: string[];
};

type ProductFormDraft = {
  productId?: string;
  storeId: string;
  name: string;
  categories: string[];
  description: string;
  sizePerUnit: number;
  availableQuantity: number;
  unitOfMeasureId: number;
  priceAmount: number;
  isTaxable: boolean;
  isReturnable: boolean;
  returnPeriod?: string;
  discountPriceAmount?: number;
  discountStartDate?: string;
  discountEndDate?: string;
  wholesaleProduct: boolean;
  minimumOrderQuantity?: number;
  availability?: string;
  selectedImages: SelectedImage[];
  existingOtherNames?: string[] | string;
  attributes: EditableProductAttribute[];
  variants: Variant[];
  hasVariantsEnabled?: boolean;
  originalProductType?: number;
};

type ProductMode = {
  productType: number;
  purchaseMode: number;
};

type ProductFormInitialValues = {
  productName: string;
  productPrice: string;
  productSize: string;
  productQty: string;
  productUnit: string;
  productDesc: string;
  productDiscountPercent: string;
  productDiscountPrice: string;
  productDiscountStart: string;
  productDiscountEnd: string;
  addDiscount: boolean;
};

export type NormalizedProductFormState = {
  initialValues: ProductFormInitialValues;
  categories: { label: string; value: string }[];
  selectedImages: Array<{ url: string; index: number; otherNames: string[] }>;
  isTaxable: boolean;
  startDate?: Date;
  endDate?: Date;
  wholesaleProduct: boolean;
  minimumOrderQuantity: string;
  availability: string;
  addVariant: boolean;
  attributes: EditableProductAttribute[];
  variants: Variant[];
  eligibleForReturn: boolean;
  eligibleForReturnOption: string;
};

const MEASUREMENT_UNITS = {
  length: ["cm", "m"] as const,
  weight: ["g", "kg", "lbs"] as const,
};

const LEGACY_ATTRIBUTE_LOOKUP: Record<string, EditableAttributeMode> = {
  color: "text",
  colour: "text",
  size: "text",
  length: "length",
  weight: "weight",
};

const toNumber = (value: unknown, fallback = 0) => {
  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  return fallback;
};

const toOptionalNumber = (value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  return undefined;
};

const toIsoString = (value?: string) => {
  if (!value) {
    return undefined;
  }

  return new Date(value).toISOString();
};

const toProductMoney = (amount: number): ProductMoney => ({
  amount,
  currency: PRODUCT_CURRENCY,
});

const toDiscountPrice = (
  amount?: number,
  startDate?: string,
  endDate?: string,
): ProductDiscountPrice | null => {
  if (amount === undefined || Number.isNaN(amount)) {
    return null;
  }

  return {
    amount,
    currency: PRODUCT_CURRENCY,
    startDate,
    endDate,
  };
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const toAttributeCode = (value: string) =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

export const parseMoneyInput = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const normalizedValue = value.replace(/,/g, "").trim();

  if (!normalizedValue) {
    return undefined;
  }

  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

export const calculateDiscountedPrice = (
  priceValue: unknown,
  discountPercentValue: unknown,
) => {
  const price = parseMoneyInput(priceValue);
  const discountPercent = parseMoneyInput(discountPercentValue);

  if (
    price === undefined ||
    discountPercent === undefined ||
    price <= 0 ||
    discountPercent <= 0 ||
    discountPercent > 100
  ) {
    return "";
  }

  return (price - (discountPercent / 100) * price).toFixed(2);
};

export const calculateDiscountPercent = (
  priceValue: unknown,
  discountedPriceValue: unknown,
) => {
  const price = parseMoneyInput(priceValue);
  const discountedPrice = parseMoneyInput(discountedPriceValue);

  if (
    price === undefined ||
    discountedPrice === undefined ||
    price <= 0 ||
    discountedPrice <= 0 ||
    discountedPrice >= price
  ) {
    return "";
  }

  return Math.round(((price - discountedPrice) / price) * 100).toString();
};

export const getRequestErrorMessage = (
  requestState: any,
  fallback = "Something went wrong. Please try again.",
) =>
  requestState?.request_data?.error ||
  requestState?.request_data?.message ||
  requestState?.request_data?.response?.message ||
  requestState?.request_data?.response?.error ||
  fallback;

const isPersistedEntityId = (value: unknown) => {
  if (typeof value !== "string") {
    return false;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return false;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    normalizedValue,
  );
};

const formatVariantNameText = (value: string) =>
  normalizeWhitespace(value)
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizeMeasurementValue = (value: string) =>
  normalizeWhitespace(value).replace(/\s+/g, "");

const getMeasurementUnitGroup = (
  mode?: EditableAttributeMode,
): readonly string[] => {
  if (mode === "length") {
    return MEASUREMENT_UNITS.length;
  }

  if (mode === "weight") {
    return MEASUREMENT_UNITS.weight;
  }

  return [];
};

const buildMeasurementOptionValue = (value: string, unit: string) =>
  `${normalizeMeasurementValue(value).toLowerCase()}_${unit.toLowerCase()}`;

const buildMeasurementOptionLabel = (value: string, unit: string) =>
  `${normalizeWhitespace(value)} ${unit}`;

const parseMeasurementOption = (
  value?: string,
  label?: string,
): { rawValue: string; unit: string } | null => {
  if (typeof value === "string") {
    const match = value.match(/^(.+)_([a-z]+)$/i);

    if (match) {
      return {
        rawValue: match[1],
        unit: match[2].toLowerCase(),
      };
    }
  }

  if (typeof label === "string") {
    const labelMatch = normalizeWhitespace(label).match(/^(.+?)\s+(cm|m|g|kg|lbs)$/i);

    if (labelMatch) {
      return {
        rawValue: labelMatch[1],
        unit: labelMatch[2].toLowerCase(),
      };
    }
  }

  return null;
};

const getAttributeMode = (
  attributeName?: string,
  options: Array<{ value?: string; label?: string }> = [],
): EditableAttributeMode => {
  const normalizedName = attributeName?.toLowerCase?.().trim() ?? "";
  const directMatch = LEGACY_ATTRIBUTE_LOOKUP[normalizedName];

  if (directMatch) {
    return directMatch;
  }

  const hasLengthUnit = options.some((option) => {
    const parsed = parseMeasurementOption(option.value, option.label);
    return parsed && MEASUREMENT_UNITS.length.includes(parsed.unit as any);
  });

  if (hasLengthUnit) {
    return "length";
  }

  const hasWeightUnit = options.some((option) => {
    const parsed = parseMeasurementOption(option.value, option.label);
    return parsed && MEASUREMENT_UNITS.weight.includes(parsed.unit as any);
  });

  if (hasWeightUnit) {
    return "weight";
  }

  return "text";
};

const normalizeAttributeOption = (
  option: any,
  index: number,
  mode: EditableAttributeMode,
): EditableAttributeOption => {
  if (mode === "length" || mode === "weight") {
    const parsed = parseMeasurementOption(option?.value, option?.label);
    const defaultUnit = getMeasurementUnitGroup(mode)[0];
    const rawValue = normalizeWhitespace(parsed?.rawValue ?? option?.label ?? option?.value ?? "");
    const unit = (parsed?.unit ?? defaultUnit).toLowerCase();

    return {
      value: buildMeasurementOptionValue(rawValue, unit),
      label: buildMeasurementOptionLabel(rawValue, unit),
      rawValue,
      unit: unit as any,
      sortOrder: option?.sortOrder ?? index + 1,
    };
  }

  const normalizedValue = normalizeWhitespace(String(option?.value ?? option?.label ?? ""));
  const normalizedLabel = normalizeWhitespace(String(option?.label ?? option?.value ?? ""));

  return {
    value: normalizedValue,
    label: normalizedLabel,
    sortOrder: option?.sortOrder ?? index + 1,
  };
};

const normalizeAttributeDefinition = (
  attribute: any,
  index: number,
): EditableProductAttribute => {
  const mode = getAttributeMode(attribute?.name, Array.isArray(attribute?.options) ? attribute.options : []);
  const options = Array.isArray(attribute?.options)
    ? attribute.options.map((option: any, optionIndex: number) =>
        normalizeAttributeOption(option, optionIndex, mode),
      )
    : [];

  return {
    name: attribute?.name ?? `Attribute ${index + 1}`,
    code: attribute?.code ?? toAttributeCode(attribute?.name ?? `ATTRIBUTE_${index + 1}`),
    type: "select",
    isRequired: attribute?.isRequired ?? true,
    sortOrder: attribute?.sortOrder ?? index + 1,
    mode,
    options,
  };
};

const inferAttributesFromVariants = (variants: any[]) => {
  const attributeMap = new Map<string, EditableProductAttribute>();

  variants.forEach((variant) => {
    const attributeValues = Array.isArray(variant?.attributeValues)
      ? variant.attributeValues
      : [];

    attributeValues.forEach((attributeValue: any, index: number) => {
      const name = attributeValue?.attributeName ?? `Attribute ${index + 1}`;
      const mode = getAttributeMode(name, [
        {
          value: attributeValue?.value,
          label: attributeValue?.value,
        },
      ]);
      const code = toAttributeCode(name);
      const option = normalizeAttributeOption(
        {
          value: attributeValue?.value,
          label: attributeValue?.value,
          sortOrder: attributeValue?.sortOrder ?? index + 1,
        },
        index,
        mode,
      );
      const existingAttribute = attributeMap.get(code);

      if (!existingAttribute) {
        attributeMap.set(code, {
          name,
          code,
          type: "select",
          isRequired: true,
          sortOrder: attributeMap.size + 1,
          mode,
          options: [option],
        });
        return;
      }

      if (!existingAttribute.options.some((existingOption) => existingOption.value === option.value)) {
        existingAttribute.options.push({
          ...option,
          sortOrder: existingAttribute.options.length + 1,
        });
      }
    });
  });

  return Array.from(attributeMap.values());
};

const normalizeAttributes = (product: any): EditableProductAttribute[] => {
  if (Array.isArray(product?.attributes) && product.attributes.length > 0) {
    return product.attributes.map((attribute: any, index: number) =>
      normalizeAttributeDefinition(attribute, index),
    );
  }

  if (Array.isArray(product?.variants) && product.variants.length > 0) {
    return inferAttributesFromVariants(product.variants);
  }

  return [];
};

const sanitizeAttributes = (
  attributes: EditableProductAttribute[],
): ProductAttributeDefinition[] =>
  attributes.map((attribute, attributeIndex) => ({
    name: normalizeWhitespace(attribute.name),
    code: attribute.code?.trim() || toAttributeCode(attribute.name),
    type: "select",
    isRequired: attribute.isRequired ?? true,
    sortOrder: attribute.sortOrder ?? attributeIndex + 1,
    options: attribute.options.map((option, optionIndex) => ({
      value: option.value,
      label: option.label,
      sortOrder: option.sortOrder ?? optionIndex + 1,
    })),
  }));

const getVariantImages = (variant: any) => {
  if (Array.isArray(variant.images)) {
    return variant.images;
  }

  if (Array.isArray(variant.urls) && variant.urls.length > 0) {
    return variant.urls.map((url: string) => ({ url }));
  }

  return [];
};

const getVariantUrls = (variant: any) => {
  if (Array.isArray(variant.urls) && variant.urls.length > 0) {
    return variant.urls.filter(Boolean);
  }

  if (Array.isArray(variant.images) && variant.images.length > 0) {
    return variant.images
      .map((image: any) => image?.url)
      .filter((url: unknown): url is string => Boolean(url));
  }

  return [];
};

const buildAttributeValuesForVariant = (variant: Variant) => {
  if (variant.attributeValues && variant.attributeValues.length > 0) {
    return variant.attributeValues.map((attributeValue, index) => ({
      attributeName: attributeValue.attributeName,
      value: attributeValue.value,
      sortOrder: attributeValue.sortOrder ?? index + 1,
    }));
  }

  if (variant.category && variant.value) {
    return [
      {
        attributeName: normalizeWhitespace(variant.category),
        value: variant.value,
        sortOrder: 1,
      },
    ];
  }

  return [];
};

const buildVariantCombinationLabel = (variant: Variant, attributes: EditableProductAttribute[] = []) =>
  buildAttributeValuesForVariant(variant)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((attributeValue) => {
      const attribute = attributes.find(
        (item) =>
          item.name.toLowerCase() === attributeValue.attributeName.toLowerCase(),
      );
      const option = attribute?.options.find(
        (optionItem) => optionItem.value === attributeValue.value,
      );

      return option?.label || attributeValue.value;
    })
    .filter(Boolean)
    .join(" / ");

export const buildVariantDisplayName = (
  productName: string,
  combinationLabel: string,
  fallbackName?: string,
  fallbackIndex?: number,
) => {
  const normalizedProductName = formatVariantNameText(productName || "");
  const normalizedCombinationLabel = normalizeWhitespace(combinationLabel || "");
  const normalizedFallbackName = formatVariantNameText(fallbackName || "");

  if (normalizedProductName && normalizedCombinationLabel) {
    return `${normalizedProductName} - ${normalizedCombinationLabel}`;
  }

  if (normalizedProductName) {
    return normalizedProductName;
  }

  if (normalizedCombinationLabel) {
    return normalizedCombinationLabel;
  }

  if (normalizedFallbackName) {
    return normalizedFallbackName;
  }

  return `Variant ${(fallbackIndex ?? 0) + 1}`;
};

const buildVariantCombinationKey = (variant: Variant) =>
  buildAttributeValuesForVariant(variant)
    .map((attributeValue) => ({
      attributeName: attributeValue.attributeName.toLowerCase(),
      value: String(attributeValue.value).toLowerCase(),
    }))
    .sort((left, right) =>
      `${left.attributeName}:${left.value}`.localeCompare(
        `${right.attributeName}:${right.value}`,
      ),
    )
    .map((attributeValue) => `${attributeValue.attributeName}:${attributeValue.value}`)
    .join("|");

const buildVariantPayloads = (
  productName: string,
  sizePerUnit: number,
  variants: Variant[],
  attributes: EditableProductAttribute[],
  includePersistedIds = false,
) =>
  variants.map<ProductVariantPayload>((variant, index) => {
    const combinationLabel =
      buildVariantCombinationLabel(variant, attributes) ||
      `Variant ${index + 1}`;
    const persistedVariantId = includePersistedIds && isPersistedEntityId(variant.id)
      ? variant.id?.trim()
      : undefined;

    return {
      ...(persistedVariantId ? { id: persistedVariantId } : {}),
      sku:
        variant.sku ||
        `${slugify(productName)}-${slugify(combinationLabel)}-${index + 1}`,
      name: variant.name || `${productName} ${combinationLabel}`.trim(),
      availableQuantity:
        toOptionalNumber(variant.availableQuantity) ?? toNumber(variant.stockQuantity),
      sizePerUnit: toOptionalNumber(variant.sizePerUnit) ?? sizePerUnit,
      weight: toOptionalNumber(variant.weight),
      isActive: variant.isActive ?? true,
      sortOrder: variant.sortOrder ?? index + 1,
      price: toProductMoney(toNumber(variant.price?.amount)),
      discountPrice: toDiscountPrice(
        toOptionalNumber(variant.discountPrice?.amount),
        toIsoString(variant.discountPrice?.startDate),
        toIsoString(variant.discountPrice?.dueDate),
      ),
      attributeValues: buildAttributeValuesForVariant(variant),
      urls:
        variant.urls ??
        variant.images?.map((image: any) => image?.url).filter(Boolean) ??
        [],
    };
  });

const buildWholesaleOffer = (
  draft: ProductFormDraft,
): ProductWholesaleOffer | undefined => {
  const minimumOrderQuantity = toOptionalNumber(draft.minimumOrderQuantity);
  const availabilityMode = toOptionalNumber(draft.availability);

  if (!minimumOrderQuantity || !availabilityMode) {
    return undefined;
  }

  return {
    isEnabled: true,
    minimumOrderQuantity,
    availableQuantity: draft.availableQuantity,
    unitOfMeasureId: draft.unitOfMeasureId,
    sizePerUnit: draft.sizePerUnit,
    price: toProductMoney(draft.priceAmount),
    availabilityMode,
  };
};

export const getUniqueOtherNames = (
  selectedImages: SelectedImage[],
  existingOtherNames?: string[] | string,
) => {
  const flattenedNames = selectedImages.flatMap((image) => {
    const imageOtherNames = image.otherNames ?? image.othernames ?? [];

    return imageOtherNames.flatMap((name) =>
      String(name)
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean),
    );
  });

  const existingNames = Array.isArray(existingOtherNames)
    ? existingOtherNames
    : typeof existingOtherNames === "string"
      ? existingOtherNames.split(",")
      : [];

  return Array.from(
    new Set(
      [...flattenedNames, ...existingNames]
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
};

export const getSelectedImageUrls = (selectedImages: SelectedImage[]) =>
  selectedImages
    .map((image) => image.url)
    .filter((url): url is string => Boolean(url));

export const getVariantDisplayName = (
  variant: Variant,
  index: number,
  attributes: EditableProductAttribute[] = [],
  productName = "",
) =>
  buildVariantDisplayName(
    productName,
    buildVariantCombinationLabel(variant, attributes),
    variant.name?.trim(),
    index,
  );

export const getVariantSummaryEntries = (
  variant: Variant,
  attributes: EditableProductAttribute[] = [],
) =>
  buildAttributeValuesForVariant(variant).map((attributeValue) => {
    const attribute = attributes.find(
      (item) =>
        item.name.toLowerCase() === attributeValue.attributeName.toLowerCase(),
    );
    const option = attribute?.options.find(
      (optionItem) => optionItem.value === attributeValue.value,
    );

    return {
      key: attributeValue.attributeName,
      value: option?.label || attributeValue.value,
    };
  });

export const resolveProductMode = (
  draft: Pick<ProductFormDraft, "wholesaleProduct" | "variants" | "hasVariantsEnabled">,
): ProductMode => {
  if (draft.wholesaleProduct) {
    return {
      productType: 0,
      purchaseMode: 2,
    };
  }

  if (draft.hasVariantsEnabled && draft.variants.length > 0) {
    return {
      productType: 1,
      purchaseMode: 1,
    };
  }

  return {
    productType: 0,
    purchaseMode: 1,
  };
};

export const getProductSubmissionErrors = (draft: ProductFormDraft) => {
  const errors: string[] = [];

  if (draft.categories.length === 0) {
    errors.push("Select at least one category");
  }

  if (getSelectedImageUrls(draft.selectedImages).length === 0) {
    errors.push("Select at least one product image");
  }

  if (draft.wholesaleProduct) {
    if (!draft.priceAmount) {
      errors.push("Enter a wholesale price");
    }

    if (!draft.minimumOrderQuantity) {
      errors.push("Enter a minimum wholesale order quantity");
    }

    if (!draft.availability || draft.availability === "0") {
      errors.push("Choose a wholesale availability mode");
    }
  }

  if (!draft.wholesaleProduct && !draft.hasVariantsEnabled) {
    if (!draft.priceAmount) {
      errors.push("Enter a product price");
    }

    if (!draft.availableQuantity && draft.availableQuantity !== 0) {
      errors.push("Enter the available quantity");
    }
  }

  if (draft.hasVariantsEnabled) {
    if (draft.attributes.length === 0) {
      errors.push("Add at least one attribute");
    }

    const attributeNames = new Set<string>();
    const attributeCodes = new Set<string>();

    draft.attributes.forEach((attribute) => {
      const normalizedName = normalizeWhitespace(attribute.name).toLowerCase();
      const normalizedCode = (attribute.code || toAttributeCode(attribute.name)).toLowerCase();

      if (attributeNames.has(normalizedName)) {
        errors.push("Attribute names must be unique");
      }

      if (attributeCodes.has(normalizedCode)) {
        errors.push("Attribute codes must be unique");
      }

      attributeNames.add(normalizedName);
      attributeCodes.add(normalizedCode);

      if (attribute.type !== "select") {
        errors.push("Only select attributes are supported");
      }

      if (!attribute.options.length) {
        errors.push(`Add at least one option for ${attribute.name}`);
      }

      const optionValues = new Set<string>();
      attribute.options.forEach((option) => {
        const normalizedValue = option.value.toLowerCase();

        if (optionValues.has(normalizedValue)) {
          errors.push(`Duplicate options are not allowed for ${attribute.name}`);
        }

        optionValues.add(normalizedValue);
      });
    });

    if (draft.variants.length === 0) {
      errors.push("Add at least one variant");
    }

    const seenCombinations = new Set<string>();

    draft.variants.forEach((variant) => {
      const combinationKey = buildVariantCombinationKey(variant);
      const attributeValues = buildAttributeValuesForVariant(variant);

      if (attributeValues.length === 0) {
        errors.push("Each variant must include attribute values");
      }

      draft.attributes
        .filter((attribute) => attribute.isRequired)
        .forEach((attribute) => {
          const matchingValue = attributeValues.find(
            (attributeValue) =>
              attributeValue.attributeName.toLowerCase() ===
              attribute.name.toLowerCase(),
          );

          if (!matchingValue?.value) {
            errors.push(`Each variant must include ${attribute.name}`);
          }
        });

      attributeValues.forEach((attributeValue) => {
        const attribute = draft.attributes.find(
          (item) =>
            item.name.toLowerCase() === attributeValue.attributeName.toLowerCase(),
        );
        const hasMatchingOption = attribute?.options.some(
          (option) => option.value === attributeValue.value,
        );

        if (attribute && !hasMatchingOption) {
          errors.push(
            `${attributeValue.attributeName} must use one of the defined options`,
          );
        }
      });

      if (!variant.price?.amount) {
        errors.push("Each variant must include a price");
      }

      if (!variant.stockQuantity && variant.stockQuantity !== 0) {
        errors.push("Each variant must include available quantity");
      }

      if (combinationKey && seenCombinations.has(combinationKey)) {
        errors.push("Duplicate variant combinations are not allowed");
      }

      if (combinationKey) {
        seenCombinations.add(combinationKey);
      }
    });
  }

  return Array.from(new Set(errors));
};

export const buildProductPayload = (
  draft: ProductFormDraft,
): CreateProductPayload | UpdateProductPayload => {
  const urls = getSelectedImageUrls(draft.selectedImages);
  const otherNames = getUniqueOtherNames(
    draft.selectedImages,
    draft.existingOtherNames,
  );
  const { productType, purchaseMode } = resolveProductMode(draft);
  const hasPersistedProductId = isPersistedEntityId(draft.productId);
  const basePayload = {
    storeId: draft.storeId,
    name: draft.name.trim(),
    categories: draft.categories,
    urls,
    otherNames,
    isTaxable: draft.isTaxable,
    description: draft.description.trim(),
    unitOfMeasureId: draft.unitOfMeasureId,
    unitOfMeasurementId: draft.unitOfMeasureId,
    sizePerUnit: draft.sizePerUnit,
    isReturnable: draft.isReturnable,
    returnPeriod: draft.returnPeriod,
    productType,
    purchaseMode,
  };

  if (productType === 1) {
    return {
      ...basePayload,
      ...(hasPersistedProductId ? { id: draft.productId?.trim() } : {}),
      attributes: sanitizeAttributes(draft.attributes),
      variants: buildVariantPayloads(
        draft.name,
        draft.sizePerUnit,
        draft.variants,
        draft.attributes,
        hasPersistedProductId,
      ),
      availableQuantity: draft.availableQuantity,
      price: undefined,
      discountPrice: null,
    };
  }

  if (purchaseMode === 2) {
    return {
      ...basePayload,
      ...(hasPersistedProductId ? { id: draft.productId?.trim() } : {}),
      availableQuantity: draft.availableQuantity,
      wholesaleOffer: buildWholesaleOffer(draft),
      price: undefined,
      discountPrice: null,
    };
  }

  return {
    ...basePayload,
    ...(hasPersistedProductId ? { id: draft.productId?.trim() } : {}),
    availableQuantity: draft.availableQuantity,
    price: toProductMoney(draft.priceAmount),
    discountPrice: toDiscountPrice(
      draft.discountPriceAmount,
      draft.discountStartDate,
      draft.discountEndDate,
    ),
  };
};

const normalizeVariant = (
  variant: any,
  productName: string,
): Variant => {
  const attributeValues = Array.isArray(variant?.attributeValues)
    ? variant.attributeValues.map((attributeValue: any, index: number) => ({
        attributeName: attributeValue?.attributeName,
        value: attributeValue?.value,
        sortOrder: attributeValue?.sortOrder ?? index + 1,
      }))
    : [];
  const firstAttributeValue = attributeValues[0];
  const mode = getAttributeMode(firstAttributeValue?.attributeName, [
    {
      value: firstAttributeValue?.value,
      label: firstAttributeValue?.value,
    },
  ]);
  const parsedMeasurement = parseMeasurementOption(
    firstAttributeValue?.value,
    firstAttributeValue?.value,
  );
  const variantPrice = variant.currentPrice ?? variant.price;
  const variantDiscountPrice = variant.discountedPrice ?? variant.discountPrice;
  const priceAmount = toNumber(variantPrice?.amount);
  const discountAmount = toOptionalNumber(variantDiscountPrice?.amount);
  const hasVariantDiscount =
    variantDiscountPrice !== null &&
    variantDiscountPrice !== undefined &&
    discountAmount !== undefined &&
    discountAmount > 0;

  let discountPercentage: number | undefined;

  if (hasVariantDiscount && priceAmount > 0) {
    discountPercentage = Math.round(
      ((priceAmount - discountAmount) / priceAmount) * 100,
    );
  }

  return {
    id: variant.id,
    category:
      mode === "length"
        ? "length"
        : mode === "weight"
          ? "weight"
          : "color",
    value: firstAttributeValue?.value ?? variant.name ?? productName,
    optionalUnit: parsedMeasurement?.unit as any,
    stockQuantity: toNumber(variant.availableQuantity),
    availableQuantity: toNumber(variant.availableQuantity),
    sku: variant.sku,
    name: variant.name,
    sizePerUnit: toOptionalNumber(variant.sizePerUnit),
    weight: toOptionalNumber(variant.weight),
    isActive: variant.isActive ?? true,
    sortOrder: variant.sortOrder,
    urls: getVariantUrls(variant),
    attributeValues,
    price: {
      amount: priceAmount,
      startDate: variantPrice?.startDate,
      dueDate: variantPrice?.dueDate ?? variantPrice?.endDate,
    },
    addDiscount: hasVariantDiscount,
    discountPercentage,
    discountPrice:
      hasVariantDiscount
        ? {
            amount: discountAmount,
            startDate: variantDiscountPrice?.startDate,
            dueDate:
              variantDiscountPrice?.dueDate ?? variantDiscountPrice?.endDate,
          }
        : undefined,
    images: getVariantImages(variant),
  };
};

export const normalizeProductResponseToForm = (
  product: any,
): NormalizedProductFormState => {
  const wholesaleProduct =
    product?.purchaseMode === 2 ||
    product?.isWholesale === true ||
    product?.wholesaleOffer?.isEnabled === true;
  const attributes = normalizeAttributes(product);
  const variants = Array.isArray(product?.variants)
    ? product.variants.map((variant: any) =>
        normalizeVariant(variant, product?.name ?? ""),
      )
    : [];
  const addVariant =
    (product?.productType === 1 || variants.length > 0) && !wholesaleProduct;
  const priceAmount = wholesaleProduct
    ? toOptionalNumber(product?.wholesaleOffer?.price?.amount) ??
      toNumber(product?.price?.amount)
    : toNumber(product?.price?.amount);
  const availableQuantity = wholesaleProduct
    ? toOptionalNumber(product?.wholesaleOffer?.availableQuantity) ??
      toNumber(product?.availableQuantity)
    : toNumber(product?.availableQuantity);
  const discountStartDate = product?.discountPrice?.startDate
    ? new Date(product.discountPrice.startDate)
    : undefined;
  const discountEndDate = product?.discountPrice?.endDate
    ? new Date(product.discountPrice.endDate)
    : undefined;
  const discountAmount = toOptionalNumber(product?.discountPrice?.amount);
  const hasProductDiscount =
    product?.discountPrice !== null &&
    product?.discountPrice !== undefined &&
    discountAmount !== undefined &&
    discountAmount > 0;
  const discountPercentage =
    hasProductDiscount && priceAmount > 0
      ? Math.round(((priceAmount - discountAmount) / priceAmount) * 100)
      : "";
  const imageOtherNames = getUniqueOtherNames([], product?.otherNames);

  return {
    initialValues: {
      productName: product?.name ?? "",
      productPrice: priceAmount ? priceAmount.toString() : "",
      productSize: toNumber(
        product?.sizePerUnit ?? product?.wholesaleOffer?.sizePerUnit,
      ).toString(),
      productQty: availableQuantity.toString(),
      productUnit: String(
        product?.unitOfMeasureId ??
          product?.unitOfMeasurementId ??
          product?.wholesaleOffer?.unitOfMeasureId ??
          "",
      ),
      productDesc: product?.description ?? "",
      productDiscountPercent:
        addVariant || discountPercentage === "" ? "" : String(discountPercentage),
      productDiscountPrice:
        !addVariant && hasProductDiscount ? discountAmount.toString() : "",
      productDiscountStart: discountStartDate?.toISOString() ?? "",
      productDiscountEnd: discountEndDate?.toISOString() ?? "",
      addDiscount: !addVariant && hasProductDiscount,
    },
    categories: Array.isArray(product?.categories)
      ? product.categories.map((category: string) => ({
          label: category,
          value: category,
        }))
      : [],
    selectedImages: Array.isArray(product?.urls)
      ? product.urls.map((url: string, index: number) => ({
          url,
          index,
          otherNames: imageOtherNames,
        }))
      : [],
    isTaxable: Boolean(product?.isTaxable),
    startDate: discountStartDate,
    endDate: discountEndDate,
    wholesaleProduct,
    minimumOrderQuantity: String(
      product?.wholesaleOffer?.minimumOrderQuantity ??
        product?.minimumOrderQuantity ??
        "",
    ),
    availability: String(
      product?.wholesaleOffer?.availabilityMode ?? product?.availability ?? "0",
    ),
    addVariant,
    attributes,
    variants,
    eligibleForReturn: Boolean(product?.isReturnable),
    eligibleForReturnOption: String(product?.returnPeriod ?? "0"),
  };
};
